import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ExperimentationSplit, ExperimentationToggle } from '@interfaces';
import { Store } from '@ngrx/store';
import { LogError, LogInfo, State } from '@store/index';
import { IFeatureToggle } from '@store/reducers/feature/feature.reducer';
import { catchError, filter, from, Observable, of, switchMap } from 'rxjs';

import { WindowRef } from '../window-ref';

@Injectable({
  providedIn: 'root',
})
export class GatekeeperService {
  static maxRetries: 25;
  static delayMs: 100;
  constructor(
    private location: Location,
    private httpClient: HttpClient,
    private windowRef: WindowRef,
    private globalStore: Store<State>,
  ) {}

  /**
   * This recursive function awaits the ccc js script to load the _satellite object which allows us to pull visitorId
   */
  private async getVisitorId(retryCount = 0): Promise<string | null> {
    const visitorId: string | undefined = this.windowRef.nativeWindow?._satellite
      ?.getVisitorId()
      ?.getMarketingCloudVisitorID();
    switch (true) {
      // If we get the visitorId, move on!
      case !!visitorId:
        this.globalStore.dispatch(
          LogInfo({
            payload: {
              eventId: 'experimentation-guard',
              retryCount,
            },
          }),
        );
        return visitorId;
      // If we reached the max retry count, proceed without visitorId
      case retryCount >= GatekeeperService.maxRetries:
        this.globalStore.dispatch(
          LogError({
            payload: {
              eventId: 'experimentation-guard--max-retries',
              message: `We hit the max retries waiting for _satellite (adobe analytics script) object`,
            },
          }),
        );
        return null;
      // Delay then try again
      default:
        await new Promise((res) => setTimeout(res, GatekeeperService.delayMs));
        return this.getVisitorId(retryCount + 1);
    }
  }

  getExperimentationToggle(
    featureId: ExperimentationToggle,
    split: ExperimentationSplit,
  ): Observable<boolean> {
    switch (split) {
      // Retrieves visitorId prior to make network call
      case ExperimentationSplit.VISITOR_ID: {
        return from(this.getVisitorId()).pipe(
          switchMap((visitorId) => {
            if (visitorId === null) return of(false);
            return this.httpClient.get<boolean>(`gatekeeper`, { params: { featureId, visitorId } });
          }),
          catchError(() => {
            return of(false);
          }),
        );
      }
      // POID is included in HTTP headers, no need to pass explicitly like visitorId
      case ExperimentationSplit.POID: {
        return this.getGatekeeper(featureId);
      }
      /* istanbul ignore next */
      default:
        const _: never = split;
        throw new Error('INVALID_EXPERIMENT_SPLIT_TYPE');
    }
  }

  getGatekeeper(featureId: string): Observable<boolean> {
    const gatekeeperUrl = this.location.prepareExternalUrl(`gatekeeper`);
    return this.httpClient
      .get<boolean>(gatekeeperUrl, { params: { featureId } })
      .pipe(catchError(() => of(false)));
  }

  getGatekeeperMulti(featureIds: string[]): Observable<IFeatureToggle[]> {
    return this.httpClient
      .get<IFeatureToggle[]>(`gatekeeper/multi`, {
        params: { featureIds },
        withCredentials: false,
      })
      .pipe(catchError(() => of(featureIds.map((x) => ({ featureId: x, enabled: false })))));
  }
}
